import { Toaster, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { FC, PropsWithChildren, useState, useCallback, useEffect, useRef } from 'react';
import { ForceTheme } from '../Components/Common/ForceTheme';
import AppMenu from '../Components/Menu/AppMenu';
import AppMenuMobile from '../Components/Menu/AppMenuMobile';
import { useIsLandscapeOnMobile, useReponsive } from '../Hooks/useResponsive';
import { PageTurnYourDevice } from './PageTurnYourDevice';
import { IGptConfiguration } from '../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useNavigate } from 'react-router-dom';
import { buildRoute, ChatRoute, ChatSpecificRoute } from '../Routes';
import { addConversationAction } from '../store/actions/conversationActions';
import { useConversations } from '../store/hooks';
import { SettingsMenuContextProvider } from '../Contexts/SettingsMenuContextProvider';
import HeaderHandler from '../Components/Header/HeaderHandler';
import SurveyHandler from '../Components/Survey/SurveyHandler';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        ...shorthands.overflow('hidden'),

        position: 'relative',
    },
    header: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100, // Above the sidebar
        backgroundColor: tokens.colorNeutralBackground1,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    mainLayout: {
        display: 'flex',
        flexDirection: 'row',
        height: '100vh',
        paddingTop: '56px', // Space for the header (adjust according to header height)
        ...shorthands.overflow('hidden'),
    },
    sidebar: {
        position: 'fixed',
        top: '56px', // Starts below the header
        left: 0,
        height: 'calc(100vh - 56px)', // Height adjusted for the header
        width: '290px',
        maxWidth: '290px', // Enforce maximum width
        minWidth: '290px', // Enforce minimum width
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.overflow('hidden'), // Clip content during transition
        zIndex: 1000,
        // Smooth slide animation using transform
        ...shorthands.transition('transform', '0.4s', 'ease-in-out'),
        transform: 'translateX(0)', // Default open position
        willChange: 'transform', // Optimize for transform animations
    },
    sidebarNoTransition: {
        transitionDuration: '0s !important', // Disable transition when switching modes
    },
    sidebarClosed: {
        // Fully hide the sidebar off-screen when closed
        transform: 'translateX(-100%)',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        /* Use remaining space to the right of the fixed-width sidebar */
        width: 'calc(100% - 290px)',
        minHeight: '0',
        flex: 1,
        ...shorthands.overflow('hidden', 'auto'),
        ...shorthands.transition('margin-left', '0.4s', 'ease-in-out'),
        marginLeft: '290px',
    },
    stickyHeaderWrapper: {
        position: 'sticky',
        top: 0,
        zIndex: 1100,
    },
    contentInner: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
    },
    contentNoTransition: {
        transitionDuration: '0s !important', // Disable transition when switching modes
    },
    contentExpanded: {
        // When menu is closed, content should use full width
        marginLeft: '0',
    }
});

/* Mobile styles */
const useStylesMobile = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - var(--vh-offset, 0px))',
        ...shorthands.overflow('hidden'),
        position: 'relative',
    },
    header: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        backgroundColor: tokens.colorNeutralBackground1,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        ...shorthands.transition('transform', '0.4s', 'ease-in-out'),
        transform: 'translateX(0)',
    },
    mainLayout: {
        display: 'flex',
        flexDirection: 'row',
        height: 'calc(100vh - var(--vh-offset, 0px))',
        ...shorthands.overflow('hidden'),
    },
    sidebar: {
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        width: 'min(calc(100vw * 2 / 3), 290px)', // 2/3 of screen width, max 290px
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.overflow('hidden'), // Clip content during transition
        zIndex: 1000,
        ...shorthands.transition('transform', '0.4s', 'ease-in-out'),
        transform: 'translateX(0)',
        willChange: 'transform', // Optimize for transform animations
    },
    sidebarNoTransition: {
        transitionDuration: '0s !important', // Disable transition when switching modes
    },
    sidebarClosed: {
        transform: 'translateX(-100%)',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        ...shorthands.flex(1),
        ...shorthands.overflow('hidden', 'auto'),
        ...shorthands.transition('transform', '0.4s', 'ease-in-out'),
        transform: 'translateX(0)', // Default Position (closed menu )
    },
    stickyHeaderWrapper: {
        position: 'sticky',
        top: 0,
        zIndex: 1100,
    },
    contentInner: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
    },
    contentNoTransition: {
        transitionDuration: '0s !important', // Disable transition when switching modes
    },
    contentExpanded: {
        transform: 'translateX(min(calc(100vw * 2 / 3), 290px))', // Shifted by sidebar width
    }
});

interface PageLayoutProps {
    menuDarkTheme?: boolean;
}

export const PageLayout: FC<PropsWithChildren<PageLayoutProps>> = (props) => {
    const { children, menuDarkTheme } = props;
    const { isMobile, value: classes } = useReponsive(useStyles(), useStylesMobile());
    const isMobileLandscape = useIsLandscapeOnMobile();
    const { dispatch } = useConversations();
    const navigate = useNavigate();
    const previousIsMobile = useRef(isMobile);

    // Menu should start closed on mobile, open on desktop (only on initial mount)
    const [isMenuOpen, setIsMenuOpen] = useState(!isMobile);
    const [isSwitchingMode, setIsSwitchingMode] = useState(false);

    // Only close menu automatically when switching TO mobile mode
    useEffect(() => {
        // Check if we're transitioning between mobile/desktop
        if (previousIsMobile.current !== isMobile) {
            // Flag that we're switching modes to disable transitions
            setIsSwitchingMode(true);

            if (isMobile) {
                // Switching to mobile - close the menu
                setIsMenuOpen(false);
            } else {
                // Switching to desktop - open the menu
                setIsMenuOpen(true);
            }
            // Update the ref for next time
            previousIsMobile.current = isMobile;

            // Re-enable transitions after a brief delay
            setTimeout(() => {
                setIsSwitchingMode(false);
            }, 50);
        }
    }, [isMobile]);


    // New API: toggleMenu receives desired open state
    const toggleMenu = (open?: boolean) => {
        if (typeof open === 'boolean') {
            setIsMenuOpen(open);
        } else {
            setIsMenuOpen(!isMenuOpen);
        }
    };

    const handleNewDraftChat = useCallback((gpt: IGptConfiguration) => {
        // Create a draft conversation locally without calling the backend
        const draftConversation = {
            id: 'DRAFT',
            userName: '',
            startedOn: new Date(),
            currentQuery: '',
            prompts: [],
            title: '',
            llm: '',
            lastUpdateOn: undefined,
            draft: true,
            gpt: {
                id: gpt?.id,
                type: gpt?.type
            }
        };

        dispatch(addConversationAction({ ...draftConversation }));
        navigate(ChatRoute);
    }, [dispatch, navigate]);

    return isMobileLandscape ? (
        <PageTurnYourDevice />
    ) : (
        <SettingsMenuContextProvider>
            <div className={classes.root}>

                {/* Header positionné au-dessus de tout */}
                {!isMobile ? (<div className={classes.header}>
                    <HeaderHandler
                        onMenuToggle={toggleMenu}
                        isMenuOpen={isMenuOpen}
                        onNewChat={handleNewDraftChat}
                        isMobile={false}
                    />
                </div>) : (<>   </>)}

                <Toaster limit={3} position='top-end' />

                {/* Layout principal sous le header */}
                <div className={classes.mainLayout}>

                    {isMobile ? (
                        <div className={mergeClasses(
                            classes.sidebar,
                            !isMenuOpen && classes.sidebarClosed,
                            isSwitchingMode && classes.sidebarNoTransition
                        )}>
                            <ForceTheme theme={menuDarkTheme ? 'dark' : undefined}>
                                <AppMenuMobile
                                    onNewChat={handleNewDraftChat}
                                    onCloseMenuRequired={() => toggleMenu(false)}
                                />
                            </ForceTheme>
                        </div>
                    ) : (
                        <div className={mergeClasses(
                            classes.sidebar,
                            !isMenuOpen && classes.sidebarClosed,
                            isSwitchingMode && classes.sidebarNoTransition
                        )}>
                            <ForceTheme theme={menuDarkTheme ? 'dark' : undefined}>
                                <AppMenu onNewChat={handleNewDraftChat} />
                            </ForceTheme>
                        </div>
                    )}

                    <div className={mergeClasses(
                        classes.content,
                        isMobile && isMenuOpen && classes.contentExpanded,
                        !isMobile && !isMenuOpen && classes.contentExpanded,
                        isSwitchingMode && classes.contentNoTransition
                    )}>
                        {isMobile ? (<div className={classes.stickyHeaderWrapper}>
                            <HeaderHandler
                                onMenuToggle={toggleMenu}
                                isMenuOpen={isMenuOpen}
                                onNewChat={handleNewDraftChat}
                                isMobile={true}
                            />
                        </div>) : null}
                        <div className={classes.contentInner}>
                            {children}
                            <SurveyHandler />
                        </div>
                    </div>
                </div>
            </div>
        </SettingsMenuContextProvider>
    );
};
